// SavingsComparisonHeroVisual — the active homepage hero visual.
//
// Replaces the legacy "CanDo Dashboard" card (preserved at
// components/hero/LegacyDashboardHeroVisual.jsx). Communicates the core
// message: CanDo helps businesses stop overpaying for card payments.
// Hybrid of a payment-approval interface, a floating savings figure,
// a current-provider-vs-CanDo comparison, and trust chips.
//
// Matches the legacy visual's footprint (relative w-full max-w-md),
// rounded-2xl card, shadow-2xl, brand border, and Framer Motion
// entrance pattern. No new dependencies.
import { motion } from "framer-motion";

const cardInitial = { opacity: 0, y: 30 };
const cardAnimate = { opacity: 1, y: 0 };
const cardTransition = { duration: 0.6, delay: 0.3 };

const approvedInitial = { opacity: 0, scale: 0.96 };
const approvedAnimate = { opacity: 1, scale: 1 };
const approvedTransition = { delay: 0.55, duration: 0.4 };

const badgeInitial = { opacity: 0, scale: 0.8, x: 20 };
const badgeAnimate = { opacity: 1, scale: 1, x: 0 };
const badgeTransition = { delay: 1.2, type: "spring", stiffness: 200 };

const rowInitial = { opacity: 0, x: -12 };
const rowAnimate = { opacity: 1, x: 0 };
const rowTransition = (i) => ({ delay: 0.75 + i * 0.15, duration: 0.4 });

const COMPARISON = [
  { id: "fees", before: "Hidden fees", after: "£0 hidden fees" },
  { id: "support", before: "Slow support", after: "4 min avg response" },
  { id: "rates", before: "Confusing rates", after: "Clear pricing" },
];

const CHIPS = ["No hidden fees", "UK support", "Powered by Elavon"];

function CheckIcon({ size = 14, stroke = "#19a50f" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 7l3 3 5-6" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Pure-CSS mini card terminal — no image asset.
function MiniTerminal() {
  return (
    <div className="flex flex-col items-center gap-1" aria-hidden="true">
      <div className="w-12 h-[4.25rem] rounded-lg bg-brand-black flex flex-col items-center pt-1.5 gap-1.5 shadow-md">
        <div className="w-9 h-5 rounded-[3px] bg-brand-aqua-light flex items-center justify-center">
          <CheckIcon size={10} stroke="#0f8a7e" />
        </div>
        <div className="grid grid-cols-3 gap-[3px]">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={`k${i}`} className="w-1.5 h-1.5 rounded-full bg-white/30" />
          ))}
        </div>
      </div>
      <div className="w-8 h-1 rounded-full bg-brand-light-grey" />
    </div>
  );
}

export default function SavingsComparisonHeroVisual() {
  return (
    <div className="relative w-full max-w-md mx-auto" data-testid="hero-visual">
      <motion.div
        initial={cardInitial}
        animate={cardAnimate}
        transition={cardTransition}
        className="bg-white rounded-2xl shadow-2xl border border-brand-light-grey/60 overflow-hidden"
        data-testid="hero-savings-visual"
      >
        {/* Black header bar — mirrors the legacy visual */}
        <div className="bg-brand-black px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-brand-aqua" />
            <span className="text-white text-xs font-semibold tracking-wide">CanDo Savings Check</span>
          </div>
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full bg-white/20" />
            <div className="w-2 h-2 rounded-full bg-white/20" />
            <div className="w-2 h-2 rounded-full bg-white/20" />
          </div>
        </div>

        {/* Payment approval */}
        <motion.div
          initial={approvedInitial}
          animate={approvedAnimate}
          transition={approvedTransition}
          className="px-5 pt-5 pb-2.5 flex items-center gap-4"
          data-testid="hero-payment-approved"
        >
          <div className="flex-1">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-5 h-5 rounded-full bg-brand-green/10 flex items-center justify-center">
                <CheckIcon size={12} />
              </span>
              <p className="text-xs font-bold text-brand-green">Savings verified</p>
            </div>
            <p className="text-3xl font-heading font-bold text-brand-black leading-tight">£42.80</p>
            <p className="text-[11px] text-brand-grey mt-0.5">Your monthly fees with CanDo</p>
            <p className="text-sm text-brand-grey line-through mt-0.5">was £228.80</p>
          </div>
          <MiniTerminal />
        </motion.div>

        {/* Processing bridge — links the approved payment to the comparison */}
        <div className="px-5">
          <p className="text-[10px] text-brand-grey">Based on your last 30 days</p>
          <div className="h-1 rounded-full bg-brand-light-grey/60 overflow-hidden mt-1">
            <div className="h-full w-full rounded-full bg-brand-green" />
          </div>
        </div>

        {/* Current provider vs CanDo */}
        <div className="px-5 pt-2.5 pb-4" data-testid="hero-comparison-panel">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] uppercase tracking-widest text-brand-grey">Current provider</p>
            <p className="text-[10px] uppercase tracking-widest text-brand-aqua font-semibold">CanDo</p>
          </div>
          <div className="space-y-2">
            {COMPARISON.map((row, i) => (
              <motion.div
                key={row.id}
                initial={rowInitial}
                animate={rowAnimate}
                transition={rowTransition(i)}
                className="flex items-center justify-between gap-3 text-xs"
              >
                <span className="flex items-center gap-1.5 text-brand-grey line-through decoration-brand-pink/50">
                  <span className="w-3.5 h-3.5 rounded-full bg-brand-pink-light flex items-center justify-center text-brand-pink text-[9px] font-bold leading-none">x</span>
                  {row.before}
                </span>
                <span className="flex items-center gap-1.5 font-semibold text-brand-black">
                  <span className="w-3.5 h-3.5 rounded-full bg-brand-green/10 flex items-center justify-center">
                    <CheckIcon size={9} />
                  </span>
                  {row.after}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Trust chips */}
        <div className="px-5 pb-5 flex flex-wrap gap-1.5">
          {CHIPS.map((chip) => (
            <span
              key={chip}
              className="text-[10px] font-semibold text-brand-aqua bg-brand-aqua-light px-2.5 py-1 rounded-full"
            >
              {chip}
            </span>
          ))}
        </div>
      </motion.div>

      {/* Floating savings badge — hangs off the card's bottom-right edge,
          sits above the card and clear of the "Clear pricing" comparison row */}
      <motion.div
        initial={badgeInitial}
        animate={badgeAnimate}
        transition={badgeTransition}
        className="absolute -bottom-8 -right-3 z-10 bg-white rounded-xl shadow-lg border border-brand-light-grey/60 px-4 py-3 text-center"
        data-testid="hero-savings-badge"
      >
        <p className="text-[10px] uppercase tracking-widest text-brand-grey">Typical saving</p>
        <p className="text-2xl font-heading font-bold text-brand-pink leading-tight">£186/mo</p>
        <p className="text-[9px] text-brand-grey">vs previous provider</p>
      </motion.div>
    </div>
  );
}
